import { Controller } from "@hotwired/stimulus"
import fountain from "../vendor/fountain"

export default class extends Controller {
  static targets = [
    "textarea",
    "editorArea",
    "previewArea",
    "titlePage",
    "mainScript",
    "previewBtn",
    "fountainModal",
    "form",
    "saveStatus"
  ]

  static values = {
    projectTitle: String,
    previewing: { type: Boolean, default: false },
    saveUrl: String
  }

  connect() {
    // Initialize Lucide icons
    if (window.lucide) {
      window.lucide.createIcons()
    }

    // Setup autosave
    this.autosaveTimeout = null
    if (this.hasTextareaTarget) {
      this.textareaTarget.addEventListener("input", () => this.scheduleAutosave())
    }
  }

  disconnect() {
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
    }
  }

  scheduleAutosave() {
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
    }
    this.autosaveTimeout = setTimeout(() => this.autosave(), 2000)
  }

  async autosave() {
    if (!this.hasTextareaTarget || !this.hasSaveStatusTarget) return

    this.showSaveStatus("Saving...")

    try {
      const response = await fetch(this.formTarget.action, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          script: { body: this.textareaTarget.value }
        })
      })

      if (response.ok) {
        this.showSaveStatus("Saved")
        setTimeout(() => this.hideSaveStatus(), 2000)
      } else {
        this.showSaveStatus("Save failed", true)
      }
    } catch (error) {
      console.error("Autosave error:", error)
      this.showSaveStatus("Save failed", true)
    }
  }

  showSaveStatus(text, isError = false) {
    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.textContent = text
      this.saveStatusTarget.classList.remove("hidden", "text-red-600", "text-gray-500")
      this.saveStatusTarget.classList.add(isError ? "text-red-600" : "text-gray-500")
    }
  }

  hideSaveStatus() {
    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.classList.add("hidden")
    }
  }

  togglePreview() {
    if (this.previewingValue) {
      // Switch to edit mode
      this.previewAreaTarget.classList.add("hidden")
      this.editorAreaTarget.classList.remove("hidden")
      this.previewBtnTarget.textContent = "Preview screenplay format"
      this.previewingValue = false
    } else {
      // Switch to preview mode
      this.renderPreview()
      this.editorAreaTarget.classList.add("hidden")
      this.previewAreaTarget.classList.remove("hidden")
      this.previewBtnTarget.textContent = "Edit"
      this.previewingValue = true
    }
  }

  renderPreview() {
    const text = this.textareaTarget.value
    if (!text) return

    try {
      const output = fountain.parse(text)
      this.titlePageTarget.innerHTML = output.html.title_page || ""
      this.mainScriptTarget.innerHTML = output.html.script || ""
    } catch (e) {
      console.error("Fountain parsing error:", e)
      this.mainScriptTarget.innerHTML = "<p class='text-red-500'>Error parsing screenplay format</p>"
    }
  }

  downloadPdf() {
    const text = this.textareaTarget.value
    if (!text) {
      alert("Please write some content before downloading")
      return
    }

    if (typeof AWPDF === "undefined") {
      alert("PDF library not loaded. Please refresh the page and try again.")
      return
    }

    try {
      AWPDF.setScript(text)
      AWPDF.getPdf((result) => {
        // Create download link
        const filename = `${this.projectTitleValue.replace(/\s+/g, "_")}_screenplay.pdf`
        this.saveBlob(result.blob, filename)
      })
    } catch (e) {
      console.error("PDF generation error:", e)
      alert("Error generating PDF. Please try again.")
    }
  }

  saveBlob(blob, filename) {
    // Use FileSaver if available, otherwise use native download
    if (typeof saveAs !== "undefined") {
      saveAs(blob, filename)
    } else {
      // Native fallback
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  showFountainInfo() {
    this.fountainModalTarget.classList.remove("hidden")
    if (window.lucide) {
      window.lucide.createIcons()
    }
  }

  closeFountainInfo() {
    this.fountainModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
}
